import { create, useStore } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { toast } from '@leadsnavi/ui-kit';
import { replyTogglePut, getAutoReplySettingGet } from '@/services/swagger/easourcing/TaskReplyService';
import { Storage } from '@/utils/storage';

const REPLY_AGENT_ENABLED_KEY = 'reply_agent_enabled';

interface ReplyAgentStoreState {
  /** 是否开启全局 AI 自动回复（localStorage 持久化） */
  enabled: boolean;
  /** 操作中防止重复点击 */
  toggling: boolean;
  /** 开关操作 */
  toggle: () => Promise<void>;
  /** 直接设置状态（供接口回调或初始化使用） */
  setEnabled: (value: boolean) => void;
  /** 调用 getAutoReplySettingGet 从服务端初始化自动回复状态 */
  initAutoReplySetting: () => Promise<void>;
}

export const replyAgentStore = create<ReplyAgentStoreState>()(
  immer(set => ({
    enabled: Storage.getItem<boolean>(REPLY_AGENT_ENABLED_KEY) ?? false,
    toggling: false,

    toggle: async () => {
      const current = replyAgentStore.getState();
      if (current.toggling) return;

      const nextEnabled = !current.enabled;

      // 乐观更新
      set({ enabled: nextEnabled, toggling: true });
      Storage.setItem(REPLY_AGENT_ENABLED_KEY, nextEnabled);

      try {
        await replyTogglePut({ enabled: nextEnabled ? 1 : 0 });
      } catch (e) {
        // 接口失败时回滚
        set({ enabled: current.enabled });
        Storage.setItem(REPLY_AGENT_ENABLED_KEY, current.enabled);
        toast.error('Failed to update Reply Agent status');
        throw e;
      } finally {
        set({ toggling: false });
      }
    },

    setEnabled: (value: boolean) => {
      set({ enabled: value });
      Storage.setItem(REPLY_AGENT_ENABLED_KEY, value);
    },

    initAutoReplySetting: async () => {
      try {
        const res = await getAutoReplySettingGet();
        const enabled = (res?.data ?? 0) === 1;
        set({ enabled });
        Storage.setItem(REPLY_AGENT_ENABLED_KEY, enabled);
      } catch {
        console.error('Failed to init auto reply setting');
      }
    },
  }))
);

/**
 * Reply Agent 全局 store 选择器 hook
 */
export const useReplyAgentStore = <T>(selector: (state: ReplyAgentStoreState) => T): T =>
  useStore(replyAgentStore, selector);

/**
 * 获取 statusText（非响应式，仅用于外部读取）
 */
export function getReplyAgentStatusText(enabled: boolean): string {
  return enabled ? 'AI is Working Now' : 'AI is off';
}
